import { Component, OnInit } from '@angular/core';
import { Subject, takeUntil } from "rxjs";
import { MenuItem, MessageService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { TranslateService } from "@ngx-translate/core";
import { AddOrEditDivisionComponent } from "./add-or-edit-division/add-or-edit-division.component";
import { DeleteDivisionComponent } from "./delete-division/delete-division.component";
import { DivisionModel } from "../../../models/divisions/division.model";
import { VenueService } from "../../../services/venues/venue.service";
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: 'app-divisions',
  templateUrl: './divisions.component.html',
  styleUrls: ['./divisions.component.scss']
})
export class DivisionsComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public listDivisions: Array<DivisionModel> = new Array<DivisionModel>();
  public spinner: boolean;
  public venueId: string;
  public items: { [name: string]: MenuItem[] } = {};
  public imagePath: string = './assets/images/logo/venue_logo_svg.svg';
  //#endregion

  //#region Constructor
  constructor(
    public dialogService: DialogService,
    public route: ActivatedRoute,
    private messageService: MessageService,
    private translate: TranslateService,
    private venueService: VenueService
  ) {
    this.translate.stream('HEADER.APP_LANGUAGE').subscribe(() => {
      this.addFunctionToAllDivisions();
    });
  }

  public ngOnInit(): void {
    this.route.params.subscribe(async params => {
      this.venueId = params['id'];
      this.loadVenueDivisions();
    });
  }
  //#endregion

  //#region Public Methods
  public loadVenueDivisions(): void {
    this.listDivisions = new Array<DivisionModel>();
    this.spinner = true;
    this.venueService.listVenueDivisions(this.venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.listDivisions = response;
          this.addFunctionToAllDivisions();
          this.spinner = false;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  public createDivision(): void {
    const ref = this.dialogService.open(AddOrEditDivisionComponent, {
      data: {
        venueId: this.venueId
      },
      width: '335px',
      header: this.translate.instant('DIVISION.CREATE_DIVISION'),
      styleClass: 'dialog-division mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadVenueDivisions();
        this.messageService.add({severity: 'success', summary: this.translate.instant("DIVISION.DIVISION_ADDED"), life: 3000});
      }
    });
  }

  public editDivision(division: DivisionModel): void {
    const ref = this.dialogService.open(AddOrEditDivisionComponent, {
      data: {
        division: JSON.parse(JSON.stringify(division)),
        venueId: this.venueId
      },
      width: '335px',
      header: this.translate.instant("DIVISION.EDIT_DIVISION"),
      styleClass: 'dialog-division mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadVenueDivisions();
        this.messageService.add({severity: 'success', summary: this.translate.instant("DIVISION.DIVISION_EDITED"), life: 3000});
      }
    });
  }

  public deleteDivision(id: string): void {
    const ref = this.dialogService.open(DeleteDivisionComponent, {
      data: {
        id: id
      },
      header: this.translate.instant("DIVISION.DELETE_DIVISION"),
      styleClass: 'dialog_long ml_200, mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadVenueDivisions();
        this.messageService.add({severity: 'success', summary: this.translate.instant("DIVISION.DIVISION_DELETED"), life: 3000});
      }
    });
  }

  public addFunctionToAllDivisions(): void {
    this.listDivisions.forEach(division => {
      this.items[division.id] = [{
        label: this.translate.instant('EDIT'),
        command: () => {
          this.editDivision(division);
        }
      },
        {
          label: this.translate.instant('DELETE'),
          command: () => {
            this.deleteDivision(division.id);
          }
        }];
    });
  }
  //#endregion
}
